import { jssPreset, StylesProvider } from "@material-ui/core";
import { create } from "jss";
import jssRTL from "jss-rtl";
import React, { FC, ReactNode } from "react";

interface Props {
  children?: ReactNode;
}

// StylesProvider's injectFirst shorthand can't be combined with a custom jss
// instance (it builds its own internally, discarding ours - and we need
// jss-rtl on ours). So this replicates injectFirst manually: a comment node
// inserted as <head>'s very first child, passed as jss's insertionPoint.
// That forces v4's JSS stylesheet ahead of any later-inserted stylesheet
// (v5's Emotion, styled-components) in the cascade for shared MUI class
// names (e.g. .MuiBackdrop-root, which both v4 and v5 emit unhashed).
// Without this, v4 JSS - only injected client-side after hydration, landing
// later in <head> than v5's SSR-extracted Emotion styles - wins instead,
// clobbering v5 component styling wherever the two coexist on a page (see
// MUI_MIGRATION_PLAN.md Risk 3). No-op during SSR, where there's no
// `document` and insertion order doesn't apply.
const getInsertionPoint = (): Comment | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const insertionPoint = document.createComment("mui-v4-jss-insertion-point");
  document.head.insertBefore(insertionPoint, document.head.firstChild);
  return insertionPoint;
};

const jss = create({
  plugins: [...jssPreset().plugins, jssRTL()],
  insertionPoint: getInsertionPoint(),
});

const MaterialRTL: FC<Props> = ({ children }) => {
  return <StylesProvider jss={jss}>{children}</StylesProvider>;
};

export { MaterialRTL };
